/**
 * iter_206 — Admin Coin Grants section. Two flows:
 *  1. Single grant — search artist by name/email → pick → enter amount + reason.
 *  2. Bulk grant — pick filter (all / active_30d) → confirm dialog with total.
 *
 * Every request stamps a fresh UUID `dedup_key` so double-tap / retry
 * short-circuits server-side (never double-grants).
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { optimizeImage } from "@/lib/mediaUrls";

const newDedupKey = () =>
  (window.crypto && window.crypto.randomUUID
    ? window.crypto.randomUUID()
    : `k_${Date.now()}_${Math.random().toString(16).slice(2)}`);

const AdminCoinGrantsSection = () => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [target, setTarget] = useState(null);
  const [amount, setAmount] = useState(50);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("single"); // "single" | "bulk"
  const [filter, setFilter] = useState("all");
  const [eligibleCount, setEligibleCount] = useState(null);
  const [bulkReceipt, setBulkReceipt] = useState(null); // iter_209 — live progress

  // Cheap search — reuse mention endpoint since it already returns users.
  useEffect(() => {
    if (tab !== "single" || !query || query.length < 2) {
      setResults([]);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/users/search-mentions?q=${encodeURIComponent(query)}&limit=8`);
        if (!cancelled) setResults(r.data?.users || r.data?.results || []);
      } catch {
        if (!cancelled) setResults([]);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query, tab]);

  const submitSingle = async () => {
    if (!target?.user_id) return toast.error("Pick an artist first");
    if (amount <= 0) return toast.error("Amount must be positive");
    if (!reason.trim()) return toast.error("Reason is required");
    setBusy(true);
    try {
      const r = await api.post("/admin/coins/grant", {
        user_id: target.user_id,
        amount: Number(amount),
        reason: reason.trim(),
        dedup_key: newDedupKey(),
      });
      if (r.data?.granted) {
        toast.success(`Granted 🪙 ${amount} to ${target.artist_name || target.name}`);
      } else if (r.data?.reason === "duplicate") {
        toast.info("This grant was already applied (duplicate).");
      } else {
        toast.warning(r.data?.reason || "Grant did not complete");
      }
      setTarget(null);
      setReason("");
      setQuery("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Grant failed");
    } finally {
      setBusy(false);
    }
  };

  // Preview count for bulk (best-effort — reuses existing users list endpoint).
  useEffect(() => {
    if (tab !== "bulk") return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/admin/users/count?filter=${filter}`).catch(() => null);
        if (!cancelled) setEligibleCount(r?.data?.count ?? null);
      } catch {
        if (!cancelled) setEligibleCount(null);
      }
    })();
    return () => { cancelled = true; };
  }, [tab, filter]);

  const totalPreview = useMemo(
    () => (eligibleCount ? eligibleCount * Number(amount || 0) : null),
    [eligibleCount, amount],
  );

  // iter_209 — Poll the bulk-grant receipt until it settles.
  useEffect(() => {
    if (!bulkReceipt?.receipt_id) return;
    if (["completed", "failed"].includes(bulkReceipt.status)) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await api.get(
          `/admin/coins/bulk-grant/${encodeURIComponent(bulkReceipt.receipt_id)}`,
        );
        if (!cancelled) setBulkReceipt(r.data);
      } catch (e) {
        // Silent — keep the last snapshot on transient failure.
      }
    };
    const iv = setInterval(poll, 2500);
    return () => { cancelled = true; clearInterval(iv); };
  }, [bulkReceipt?.receipt_id, bulkReceipt?.status]);

  const submitBulk = async () => {
    if (amount <= 0) return toast.error("Amount must be positive");
    if (!reason.trim()) return toast.error("Reason is required");
    const label = eligibleCount != null ? `${eligibleCount} artists` : "all eligible artists";
    const totalLabel = totalPreview != null ? ` = 🪙 ${totalPreview} total` : "";
    if (!window.confirm(`Grant 🪙 ${amount} to ${label}${totalLabel}. Confirm?`)) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/coins/bulk-grant", {
        amount: Number(amount),
        reason: reason.trim(),
        dedup_key: newDedupKey(),
        filter,
      });
      const d = r.data || {};
      if (d.duplicate) {
        toast.info("This bulk grant was already applied — reopening the receipt.");
      } else {
        toast.success(`Queued bulk grant for ${d.target_count} artists. Watching progress…`);
      }
      setBulkReceipt(d);
      setReason("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk grant failed");
    } finally {
      setBusy(false);
    }
  };

  const resumeBulk = async () => {
    if (!bulkReceipt?.receipt_id) return;
    setBusy(true);
    try {
      const r = await api.post(
        `/admin/coins/bulk-grant/${encodeURIComponent(bulkReceipt.receipt_id)}/resume`,
      );
      setBulkReceipt(r.data);
      toast.success("Retrying the failed bulk grant. Already-credited artists will be skipped.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resume failed");
    } finally {
      setBusy(false);
    }
  };

  const dismissReceipt = () => setBulkReceipt(null);

  const receiptInFlight = bulkReceipt && !["completed", "failed"].includes(bulkReceipt.status);
  const bulkButtonDisabled = busy || Boolean(receiptInFlight);

  return (
    <Card className="p-5 bg-black/40 border-white/10" data-testid="admin-coin-grants">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-amber-300 font-black">
            🪙 Coin Management
          </p>
          <h3 className="text-white font-black text-lg">Grant RGM Coins</h3>
          <p className="text-[11px] text-gray-500">
            All grants are logged in the ledger as `admin_coin_grant`. Duplicate dedup_keys short-circuit safely.
          </p>
        </div>
        <div className="flex bg-white/5 rounded-lg p-0.5">
          <button
            onClick={() => setTab("single")}
            className={`px-3 py-1 text-xs font-black rounded-md ${tab === "single" ? "bg-amber-500 text-black" : "text-gray-300"}`}
            data-testid="admin-coin-tab-single"
          >Single</button>
          <button
            onClick={() => setTab("bulk")}
            className={`px-3 py-1 text-xs font-black rounded-md ${tab === "bulk" ? "bg-amber-500 text-black" : "text-gray-300"}`}
            data-testid="admin-coin-tab-bulk"
          >Bulk</button>
        </div>
      </div>

      {tab === "single" && (
        <div className="space-y-3" data-testid="admin-coin-single-form">
          {!target ? (
            <>
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search artist by name or email…"
                className="bg-black/30 text-white border-white/10"
                data-testid="admin-coin-search"
              />
              {results.length > 0 && (
                <div className="space-y-1 max-h-52 overflow-y-auto pr-1">
                  {results.map((u) => (
                    <button
                      key={u.user_id}
                      onClick={() => setTarget(u)}
                      className="w-full flex items-center gap-2 p-2 rounded-lg bg-white/5 hover:bg-white/10 text-left"
                      data-testid={`admin-coin-pick-${u.user_id}`}
                    >
                      {u.profile_photo && (
                        <img src={optimizeImage(u.profile_photo, 'avatar')} alt="" className="w-7 h-7 rounded-full object-cover" />
                      )}
                      <span className="text-white text-sm">{u.artist_name || u.name}</span>
                      <span className="text-gray-500 text-xs">{u.user_id}</span>
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="flex items-center justify-between p-2 rounded-lg bg-white/5" data-testid="admin-coin-target">
              <div className="flex items-center gap-2">
                {target.profile_photo && <img src={optimizeImage(target.profile_photo, 'avatar')} alt="" className="w-8 h-8 rounded-full object-cover" />}
                <div>
                  <p className="text-white font-black text-sm">{target.artist_name || target.name}</p>
                  <p className="text-gray-500 text-xs">{target.user_id}</p>
                </div>
              </div>
              <button
                onClick={() => setTarget(null)}
                className="text-xs text-gray-400 hover:text-white"
                data-testid="admin-coin-clear-target"
              >Change</button>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <Input
              type="number"
              value={amount}
              onChange={(e) => setAmount(Number(e.target.value))}
              placeholder="Amount"
              className="bg-black/30 text-white border-white/10"
              data-testid="admin-coin-amount"
            />
            <Button
              onClick={submitSingle}
              disabled={busy || !target}
              className="bg-amber-500 hover:bg-amber-400 text-black font-black"
              data-testid="admin-coin-grant-btn"
            >
              {busy ? "Granting…" : "🪙 Grant Coins"}
            </Button>
          </div>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder="Reason (required — shows in ledger + admin audit)"
            className="bg-black/30 text-white border-white/10 text-sm"
            data-testid="admin-coin-reason"
          />
        </div>
      )}

      {tab === "bulk" && (
        <div className="space-y-3" data-testid="admin-coin-bulk-form">
          <div className="flex bg-white/5 rounded-lg p-0.5 w-fit">
            <button
              onClick={() => setFilter("all")}
              className={`px-3 py-1 text-xs font-black rounded-md ${filter === "all" ? "bg-amber-500 text-black" : "text-gray-300"}`}
              data-testid="admin-coin-filter-all"
            >All artists</button>
            <button
              onClick={() => setFilter("active_30d")}
              className={`px-3 py-1 text-xs font-black rounded-md ${filter === "active_30d" ? "bg-amber-500 text-black" : "text-gray-300"}`}
              data-testid="admin-coin-filter-active"
            >Active last 30d</button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Input
              type="number"
              value={amount}
              onChange={(e) => setAmount(Number(e.target.value))}
              placeholder="Amount per artist"
              className="bg-black/30 text-white border-white/10"
              data-testid="admin-coin-bulk-amount"
            />
            <Button
              onClick={submitBulk}
              disabled={bulkButtonDisabled}
              className="bg-amber-500 hover:bg-amber-400 text-black font-black disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="admin-coin-bulk-btn"
            >
              {busy ? "Queuing…" : receiptInFlight ? "Processing…" : "🪙 Bulk Grant"}
            </Button>
          </div>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder="Reason (required — shows in ledger + admin audit)"
            className="bg-black/30 text-white border-white/10 text-sm"
            data-testid="admin-coin-bulk-reason"
          />
          {eligibleCount != null && (
            <p className="text-[11px] text-amber-100" data-testid="admin-coin-bulk-preview">
              Preview: 🪙 {amount} × {eligibleCount} artists = 🪙 {totalPreview} total.
            </p>
          )}
          {bulkReceipt && (
            <BulkReceiptPanel
              receipt={bulkReceipt}
              onResume={resumeBulk}
              onDismiss={dismissReceipt}
              busy={busy}
            />
          )}
        </div>
      )}
    </Card>
  );
};

// iter_209 — Live progress panel for a bulk-grant receipt. Renders the
// four-state receipt (queued/processing/completed/failed), a progress
// bar with "X / N artists credited", the final breakdown, and a
// Resume button that ONLY appears when the run has failed.
const BulkReceiptPanel = ({ receipt, onResume, onDismiss, busy }) => {
  const {
    receipt_id, status, target_count = 0, processed_count = 0,
    granted = 0, skipped = 0, failed = 0, total_coins_granted = 0,
    amount, reason, filter, admin_actor, created_at, completed_at,
    last_error,
  } = receipt || {};
  const pct = target_count > 0
    ? Math.min(100, Math.round((processed_count / target_count) * 100))
    : 0;
  const statusPill = {
    queued: "bg-slate-500/20 text-slate-200 border-slate-400/40",
    processing: "bg-amber-500/20 text-amber-200 border-amber-400/40",
    completed: "bg-emerald-500/20 text-emerald-200 border-emerald-400/40",
    failed: "bg-rose-500/20 text-rose-200 border-rose-400/40",
  }[status] || "bg-slate-500/20 text-slate-200 border-slate-400/40";
  const isFailed = status === "failed";
  const isDone = status === "completed";
  return (
    <div
      className="mt-3 p-3 rounded-lg bg-black/30 border border-white/10 space-y-2"
      data-testid="admin-coin-bulk-receipt"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-[0.25em] text-gray-400">Receipt</span>
          <code className="text-xs text-amber-200" data-testid="admin-coin-bulk-receipt-id">
            {receipt_id}
          </code>
        </div>
        <span
          className={`text-[10px] uppercase tracking-[0.3em] font-black px-2 py-0.5 rounded-full border ${statusPill}`}
          data-testid="admin-coin-bulk-receipt-status"
        >
          {status}
        </span>
      </div>
      <div className="w-full bg-white/5 rounded-full h-2 overflow-hidden">
        <div
          className="bg-amber-500 h-2 transition-all"
          style={{ width: `${pct}%` }}
          data-testid="admin-coin-bulk-progress-bar"
        />
      </div>
      <p className="text-xs text-gray-300" data-testid="admin-coin-bulk-progress-label">
        <span className="font-black text-white">{processed_count.toLocaleString()}</span>
        {" / "}
        <span className="font-black text-white">{target_count.toLocaleString()}</span>
        {" artists credited"}
        {pct > 0 && <span className="text-gray-500"> · {pct}%</span>}
      </p>
      <div className="grid grid-cols-4 gap-1.5 text-center">
        <Stat label="Granted" value={granted} testid="admin-coin-bulk-granted" tone="emerald" />
        <Stat label="Skipped" value={skipped} testid="admin-coin-bulk-skipped" tone="slate" />
        <Stat label="Failed" value={failed} testid="admin-coin-bulk-failed" tone={failed > 0 ? "rose" : "slate"} />
        <Stat label="🪙 Total" value={total_coins_granted} testid="admin-coin-bulk-total" tone="amber" />
      </div>
      <div className="text-[10px] text-gray-500 leading-relaxed">
        🪙 {amount} per artist · Filter: {filter} · Admin: {admin_actor}<br />
        Reason: {reason}<br />
        Queued: {created_at}
        {completed_at && <><br />Finished: {completed_at}</>}
        {last_error && (
          <><br /><span className="text-rose-300">Last error: {last_error}</span></>
        )}
      </div>
      {isFailed && (
        <p className="text-[11px] text-rose-200 bg-rose-500/10 border border-rose-400/30 rounded p-2">
          This run failed partway. Retrying is safe — already-credited artists
          are protected by the ledger and will be skipped automatically.
        </p>
      )}
      <div className="flex gap-2">
        {isFailed && (
          <Button
            onClick={onResume}
            disabled={busy}
            className="bg-rose-500 hover:bg-rose-400 text-white font-black text-xs h-8"
            data-testid="admin-coin-bulk-resume-btn"
          >
            {busy ? "Retrying…" : "↺ Retry failed grant"}
          </Button>
        )}
        {(isDone || isFailed) && (
          <Button
            onClick={onDismiss}
            variant="outline"
            className="border-white/20 text-gray-200 hover:bg-white/5 text-xs h-8"
            data-testid="admin-coin-bulk-dismiss-btn"
          >
            Dismiss
          </Button>
        )}
        {!isDone && !isFailed && (
          <p className="text-[11px] text-amber-200/80" data-testid="admin-coin-bulk-inflight-hint">
            Processing in the background — you can leave this page and come back.
          </p>
        )}
      </div>
    </div>
  );
};

const Stat = ({ label, value, testid, tone = "slate" }) => {
  const toneCls = {
    emerald: "text-emerald-300",
    slate: "text-slate-300",
    rose: "text-rose-300",
    amber: "text-amber-300",
  }[tone];
  return (
    <div className="bg-white/5 rounded p-1.5" data-testid={testid}>
      <p className={`font-black ${toneCls}`}>{Number(value || 0).toLocaleString()}</p>
      <p className="text-[9px] uppercase tracking-widest text-gray-500">{label}</p>
    </div>
  );
};

export default AdminCoinGrantsSection;
